<script lang="ts">
  import { getContext } from 'svelte';
  import { type SanityContext, key } from '$lib/context/sanity';
  import type { PageFullWidthMedia } from '$lib/sanity/schemas';
  import { pipe } from '$lib/utils/pipe';
  import {
    toImageSrcAttributes,
    toLazyloadSrcsetUrlBuilders,
    toSrcsetUrlBuilders,
    useSanityImageLazyload
  } from '$lib/sanity/image';
  import { extractAspectRatioFromAsset } from '$lib/utils/sanity';
  import { toCSSVars } from '$lib/utils/cssVars';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import Video from '../Common/Video.svelte';
  import PageThemeProvider from '$components/Pages/PageThemeProvider.svelte';

  interface Props {
    document: PageFullWidthMedia;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  const { urlBuilder } = getContext<SanityContext>(key);
  const { isInView, lazyload } = useSanityImageLazyload();

  let srcAttrs = $derived(
    document?.image?.asset &&
      pipe(
        document.image.asset,
        toSrcsetUrlBuilders(urlBuilder),
        toLazyloadSrcsetUrlBuilders($isInView),
        toImageSrcAttributes()
      )
  );

  let aspectRatio = $derived(document.image && extractAspectRatioFromAsset(document.image));
  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));
</script>

<PageThemeProvider theme={document.theme ?? {}}>
  <div class="wrapper">
    <div>
      {#if document.image && srcAttrs}
        <img
          use:lazyload
          srcset={srcAttrs.srcset}
          src={srcAttrs.src}
          alt={document.altText}
          style={toCSSVars({ aspectRatio })}
          data-sanity={encodeDataAttribute([...path, 'image', '_type'])}
        />
      {/if}
      {#if document.video}
        <Video
          {sourceMap}
          {path}
          document={document.video}
          preload="auto"
          renditionOrder="desc"
          minResolution="1080p"
        />
      {/if}
    </div>
  </div>
</PageThemeProvider>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';
  .wrapper {
    background-color: var(--theme-general-background-color, none);
  }

  .wrapper div {
    position: relative;
    margin: var(--twist-core-spacing-0) auto;

    @include at('max') {
      max-width: $max-width-page - 2 * $max-width-margin;
    }
  }

  img,
  video {
    width: 100%;
  }
</style>
